/**
 * Human-readable labels for technical keys used across the admin UI.
 * Import and use `formatLabel(key)` to display user-friendly names.
 */

const LABEL_MAP: Record<string, string> = {
  // Case types
  sanctions_match: 'Sanctions Match',
  pep_match: 'PEP Match',
  adverse_media: 'Adverse Media',
  screening_hit: 'Screening Hit',
  beneficiary_screening: 'Beneficiary Screening',
  periodic_review: 'Periodic Review',
  doc_refresh: 'Document Refresh',
  edd: 'Enhanced Due Diligence',
  kyb_exception: 'KYB Exception',
  card_dispute: 'Card Dispute',
  aml_investigation: 'AML Investigation',
  sanctions_pep_match: 'Sanctions / PEP Match',
  suspicious_activity: 'Suspicious Activity',
  risk_review: 'Risk Review',
  ubo_change: 'UBO Change',
  high_risk_corridor: 'High Risk Corridor',
  velocity_breach: 'Velocity Breach',
  fx_anomaly: 'FX Anomaly',
  structuring: 'Structuring / Smurfing',
  transaction_enquiry: 'Transaction Enquiry',

  // Hit types
  pep: 'PEP',
  sanctions: 'Sanctions',
  adverse_media_hit: 'Adverse Media',
  compromised: 'Compromised Person',
  kyb: 'KYB',

  // Subject types
  client: 'Client',
  application: 'Application',
  beneficiary: 'Beneficiary',
  person: 'Person',
  individual: 'Individual',
  corporate: 'Corporate',

  // Client types
  personal: 'Personal',

  // Screening statuses
  clear: 'Clear',
  match: 'Match',
  potential_match: 'Potential Match',
  pending: 'Pending',
  not_checked: 'Not Checked',
  false_positive: 'False Positive',
  true_match: 'True Match',
  escalated: 'Escalated',
  confirmed: 'Confirmed',
  dismissed: 'Dismissed',
  under_review: 'Under Review',
  whitelisted: 'Whitelisted',
  inconclusive: 'Inconclusive',

  // Case statuses
  open: 'Open',
  in_progress: 'In Progress',
  in_review: 'In Review',
  closed: 'Closed',
  resolved: 'Resolved',
  paused: 'Paused',
  investigating: 'Investigating',

  // Case decisions
  true_positive: 'True Positive',
  retain_monitoring: 'Retain Under Monitoring',

  // Review statuses
  scheduled: 'Scheduled',
  due: 'Due',
  overdue: 'Overdue',
  completed: 'Completed',

  // Risk tiers
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',

  // Priorities
  urgent: 'Urgent',

  // TM alert dispositions
  triaged: 'Triaged',
  merged: 'Merged / Duplicate',

  // Escalation targets
  fraud_ops: 'Fraud Ops',
  payments_ops: 'Payments Ops',
  legal: 'Legal',
  mlro: 'MLRO',

  // Payment / compliance statuses
  compliance_hold: 'Compliance Hold',
  held_for_review: 'Held for Review',
  compliance_approved: 'Approved',
  compliance_rejected: 'Payment Cancelled',
  payment_hold: 'Payment Hold',
  sent: 'Sent',
  settled: 'Settled',
  refunded: 'Refunded',
  created: 'Created',

  // Rule modes / lifecycle
  draft: 'Draft',
  pilot: 'Pilot',
  monitor_only: 'Monitor Only',
  enforce: 'Enforce',
  active: 'Active',
  disabled: 'Disabled',
  retired: 'Retired',
  archived: 'Archived',

  // Rule families
  mcc_anomaly_cluster: 'MCC Anomaly Cluster',
  atm_cash_spike: 'ATM Cash Spike',
  ecommerce_velocity: 'E-Commerce Velocity',
  crossborder_card_anomaly: 'Cross-Border Card Anomaly',
  cnp_3ds_context: 'CNP / 3DS Context',
  bin_cluster_anomaly: 'BIN Cluster Anomaly',
  reissue_window_anomaly: 'Reissue Window Anomaly',
  volume_drift: 'Volume Drift',
  corridor_drift: 'Corridor Drift',
  currency_activation: 'Currency Activation',
  product_usage_change: 'Product Usage Change',
  alert_density_spike: 'Alert Density Spike',
  repeated_high_severity: 'Repeated High Severity',
  card_auth_pattern_change: 'Card Auth Pattern Change',
  doc_expiry_active_client: 'Document Expiry (Active Client)',
  risk_tier_mismatch: 'Risk Tier Mismatch',

  // Scheme labels
  visa: 'Visa',
  mastercard: 'Mastercard',
  cup: 'China UnionPay',

  // Card / third-party metadata keys
  scheme: 'Card Scheme',
  processor_reference: 'Processor Reference',
  processor_event_id: 'Processor Event ID',
  bureau_reference: 'Bureau Reference',
  threeds_provider: '3DS Provider',
  threeds_result: '3DS Result',
  authentication_status: 'Authentication Status',
  challenge_indicator: 'Challenge Indicator',
  card_channel: 'Card Channel',
  card_entry_mode: 'Entry Mode',
  mcc: 'MCC',
  merchant_country: 'Merchant Country',
  merchant_id: 'Merchant ID',
  card_presentment_context: 'Presentment Context',

  // PSD2 / SCA / 3DS authentication context
  sca_applied: 'SCA Applied',
  sca_exemption_type: 'SCA Exemption Type',
  liability_shift_indicator: 'Liability Shift',
  soft_decline_sca_required: 'Soft Decline (SCA Required)',

  // SCA exemption types (PSD2 Art. 10–18 RTS)
  low_value: 'Low-Value Exemption',
  low_risk_tra: 'Low-Risk TRA Exemption',
  trusted_beneficiary: 'Trusted Beneficiary',
  recurring_transaction: 'Recurring Transaction',
  secure_corporate_payment: 'Secure Corporate Payment',
  merchant_initiated: 'Merchant-Initiated Transaction',
  delegated_authentication: 'Delegated Authentication',
  no_exemption: 'No Exemption (Full SCA)',

  // 3DS authentication outcomes
  authenticated: 'Authenticated',
  not_authenticated: 'Not Authenticated',
  authentication_attempted: 'Authentication Attempted',
  authentication_unavailable: 'Authentication Unavailable',
  challenge_required: 'Challenge Required',
  frictionless: 'Frictionless',
  challenge_completed: 'Challenge Completed',
  challenge_failed: 'Challenge Failed',
  issuer_rejected: 'Issuer Rejected',

  // Liability shift
  liability_shift_yes: 'Liability Shift to Issuer',
  liability_shift_no: 'No Liability Shift',
  liability_shift_unknown: 'Liability Shift Unknown',

  // Activity log action types
  auto_approve_low_risk: 'Auto-Approved (Low Risk)',
  auto_approval_blocked_verification_failed: 'Auto-Approval Blocked — Verification Failed',
  auto_approval_blocked_high_risk: 'Auto-Approval Blocked — High Risk',
  auto_approval_blocked_missing_docs: 'Auto-Approval Blocked — Missing Documents',
  note_added: 'Note Added',
  risk_updated: 'Risk Updated',
  view: 'Viewed',
  approve: 'Approved',
  reject: 'Rejected',
  export: 'Exported',

  // Security audit actions
  mfa_requirement_change: 'MFA Requirement Change',
  backoffice_review_confirmed: 'Back-Office Review Confirmed',
  ops_threshold_update: 'Ops Threshold Update',
  role_change: 'Role Change',
  account_freeze: 'Account Freeze',
  account_unfreeze: 'Account Unfreeze',
  client_profile_update: 'Client Profile Update',

  // Compliance case actions (useActionExecutor events)
  freeze_account: 'Freeze Account',
  restrict_account: 'Restrict Account',
  unfreeze_account: 'Unfreeze / Remove Restriction',
  close_account: 'Close Account',
  escalate_edd: 'Escalate to EDD',
  escalate_mlro: 'Escalate to MLRO',
  escalate_fraud: 'Escalate to Fraud Ops',
  escalate_payments: 'Escalate to Payments Ops',
  escalate_legal: 'Escalate to Legal',
  assign_owner: 'Assign / Reassign Owner',
  set_priority: 'Set Priority',
  set_sla: 'Set SLA / Due Date',
  add_note: 'Add Investigation Note',
  request_documents: 'Request Documents (RFI)',
  mark_false_positive: 'Mark as False Positive',
  enhanced_monitoring: 'Enhanced Monitoring',
  pause_investigation: 'Pause Investigation',
  resume_investigation: 'Resume Investigation',
  link_entity: 'Link Related Entity',
  link_case: 'Link Related Case',
  hold_outbound: 'Hold Outbound Payments',
  block_rails: 'Block Payment Rails',
  hold_inbound: 'Hold Inbound Crediting',
  require_manual_review: 'Require Manual Payment Review',
  remove_hold: 'Remove Hold / Unblock',
  block_beneficiary: 'Block Beneficiary',
  block_corridor: 'Block Country / Corridor',
  freeze_all_cards: 'Freeze All Cards',
  unfreeze_cards: 'Unfreeze Cards',
  freeze_selected_cards: 'Freeze Selected Card(s)',
  block_card_mcc: 'Block Card by MCC',
  disable_cash_withdrawal: 'Disable Cash Withdrawal',
  disable_ecommerce: 'Disable E-Commerce / Cross-Border',
  reissue_card: 'Reissue Card Request',
  prepare_sar: 'Prepare SAR Draft',
  submit_sar: 'Submit SAR',
  add_legal_hold: 'Add Legal Hold',
  tipping_off_flag: 'Tipping-Off Restriction',
  add_filing_reference: 'Add Filing Reference',
  record_lea_contact: 'Record LEA / Regulator Contact',
  close_true_positive: 'Close as True Positive',
  close_false_positive: 'Close as False Positive',
  offboard_client: 'Offboard Client',
  override_risk: 'Override Risk Rating',
  follow_up_review: 'Follow-Up Review',

  // Screening queue actions (audit log)
  screening_hit_disposition: 'Screening Hit Disposition',
  screening_hit_assign: 'Screening Hit Assigned',
  screening_suppression_create: 'Screening Suppression Created',
  screening_suppression_remove: 'Screening Suppression Removed',
  screening_hit_link_case: 'Screening Hit Linked to Case',
  screening_hit_escalate: 'Screening Hit Escalated',
  screening_hit_reopen: 'Screening Hit Reopened',
  screening_bulk_disposition: 'Screening Bulk Disposition',
  screening_hit_linked: 'Screening Hit Linked',
  screening_hit_escalated: 'Screening Hit Escalated',
  screening_hit_disposition_recorded: 'Screening Hit Disposition Recorded',

  // TM alert actions (audit log)
  tm_alert_disposition: 'TM Alert Disposition',
  tm_alert_assign: 'TM Alert Assigned',
  tm_alert_escalate: 'TM Alert Escalated',
  tm_alert_link_case: 'TM Alert Linked to Case',
  tm_alert_reopen: 'TM Alert Reopened',
  tm_alert_bulk_disposition: 'TM Alert Bulk Disposition',
  tm_alert_bulk_assign: 'TM Alert Bulk Assign',
  tm_alert_linked: 'TM Alert Linked',
  tm_alert_escalated: 'TM Alert Escalated',
  tm_alert_disposition_recorded: 'TM Alert Disposition Recorded',

  // Case events
  entity_linked: 'Entity Linked',
  case_linked: 'Case Linked',
  lea_contact_recorded: 'LEA Contact Recorded',
  filing_reference_added: 'Filing Reference Added',
  alert_linked: 'Alert Linked',

  // Target types
  client_profile: 'Client Profile',
  ops_threshold: 'Ops Threshold',
  admin_user: 'Admin User',
  ledger_account: 'Ledger Account',
  compliance_case: 'Compliance Case',
  screening_hit_target: 'Screening Hit',
  tm_alert: 'TM Alert',

  // Detail field keys
  mfa_required: 'MFA Required',
  reviewed: 'Reviewed',
  session_id: 'Session',

  // Ops threshold keys
  billing_failure_rate_threshold_bps: 'Billing Failure Rate (bps)',
  pipeline_failed_threshold: 'Pipeline Failure Threshold',
  recon_dispute_tolerance_minor: 'Dispute Recon Tolerance',
  recon_persist_hours_before_case: 'Recon Persist Hours Before Case',
  recon_settlement_tolerance_minor: 'Settlement Recon Tolerance',
  tm_backlog_threshold: 'TM Backlog Threshold',
  treasury_autopilot_enabled: 'Treasury Autopilot Enabled',

  // Approval statuses
  approved: 'Approved',
  rejected: 'Rejected',
  expired: 'Expired',
  executed: 'Executed',
  execution_failed: 'Execution Failed',

  // DB table names (for descriptions)
  screening_checks: 'screening checks',
  screening_hits: 'screening hits',
  tm_alerts: 'transaction monitoring alerts',
  compliance_cases: 'compliance cases',
  periodic_reviews: 'periodic reviews',
  document_refresh_tasks: 'document refresh tasks',

  // Reporting obligation taxonomy (Phase 12B)
  psd2_fraud_stats: 'PSD2 Fraud Statistics',
  psd2_payment_services: 'PSD2 Payment Services Reporting',
  dora_ict_incident: 'DORA ICT Incident Report',
  scheme_notification: 'Scheme Notification',
  scheme_notification_visa: 'Visa Scheme Notification',
  scheme_notification_mastercard: 'Mastercard Scheme Notification',
  scheme_notification_cup: 'CUP Scheme Notification',
  regulator_inquiry: 'Regulator Inquiry / Response',
  internal_incident: 'Internal Incident Review',
  operational_review: 'Operational Review',
  sar_filing: 'SAR / STR Filing',
  regulatory_breach_report: 'Regulatory Breach Report',

  // DORA-specific labels
  dora_major_incident: 'DORA Major ICT Incident',
  dora_significant_cyber_threat: 'DORA Significant Cyber Threat',
  dora_ict_third_party: 'DORA ICT Third-Party Provider',
  dora_operational_resilience: 'DORA Operational Resilience',

  // PSD2 QA / governance labels
  sca_traceability: 'SCA Traceability',
  threeds_traceability: '3DS Traceability',
  third_party_traceability: 'Third-Party Traceability',
  reporting_timeliness: 'Reporting Timeliness',
  reporting_completeness: 'Reporting Completeness',
  closure_gate_compliance: 'Closure Gate Compliance',
  evidence_completeness: 'Evidence Completeness',
  psd2_qa_review: 'PSD2 QA Review',
  card_case_traceability: 'Card Case Traceability',
  processor_ref_missing: 'Processor Reference Missing',
  bureau_ref_missing: 'Bureau Reference Missing',
  threeds_ref_missing: '3DS Reference Missing',
  sca_evidence_missing: 'SCA Evidence Missing',

  // Phase 12C — Open Banking / TPP applicability labels
  psd2_open_banking: 'PSD2 Open Banking',
  tpp_ais: 'TPP – Account Information Service (AIS)',
  tpp_pis: 'TPP – Payment Initiation Service (PIS)',
  tpp_piis: 'TPP – Payment Instrument Issuer (PIIS / CBPII)',
  aspsp_interface: 'ASPSP Dedicated Interface',
  open_banking_not_applicable: 'Open Banking – Not Applicable',
  open_banking_externally_owned: 'Open Banking – Externally Owned',
  tpp_access_revocation: 'TPP Access Revocation',
  tpp_consent_management: 'TPP Consent Management',
  tpp_passporting: 'TPP Passporting / Registration',

  // Phase 12C — Complaints / disputes / unauthorized txn handoff labels
  complaint_reference: 'Complaint Reference',
  dispute_reference: 'Dispute Reference',
  unauthorized_txn_claim: 'Unauthorized Transaction Claim',
  handoff_complaints_team: 'Handoff to Complaints Team',
  handoff_disputes_team: 'Handoff to Disputes Team',
  handoff_fraud_ops: 'Handoff to Fraud Ops',
  handoff_legal_team: 'Handoff to Legal Team',
  consumer_rights_psd2: 'PSD2 Consumer Rights',
  refund_claim_psd2: 'PSD2 Refund Claim (Art. 73–74)',
  unauthorized_txn_liability: 'Unauthorized Txn Liability (Art. 73)',
  complaint_escalation: 'Complaint Escalation',
  complaint_resolution: 'Complaint Resolution',
  complaint_regulatory_referral: 'Complaint Regulatory Referral',

  // Phase 12C — Third-party oversight (PSD2 context) labels
  vendor_followup_owner: 'Vendor Follow-Up Owner',
  vendor_sla_reference: 'Vendor SLA Reference',
  outsourcing_register_ref: 'Outsourcing Register Reference',
  critical_service_provider: 'Critical Service Provider',
  processor_incident_ref: 'Processor Incident Reference',
  bureau_incident_ref: 'Bureau Incident Reference',
  threeds_incident_ref: '3DS Provider Incident Reference',
  // Sanctioned Countries (AUTO FAIL)
  sanctioned_countries: 'Sanctioned Countries (AUTO FAIL)',

  // Assessment types
  initial: 'Initial Assessment',
  periodic: 'Periodic Review',
  trigger_based: 'Trigger-Based Review',
  event_driven: 'Event-Driven Review',

  // Auto decisions — 5-tier system
  instant_approval: 'Auto-Approved',
  auto_approve_medium_low: 'Auto-Approved (Medium-Low)',
  conditional_approval: 'Conditional Approval',
  manual_review: 'Manual Review Required',
  manual_review_required: 'Manual Review Required',
  reject_or_edd: 'Reject / EDD Required',
  reject_immediately: 'Rejected',
  auto_approve_enhanced_monitoring: 'Auto-Approved with Enhanced Monitoring',
  edd_required: 'EDD Required',

  // Risk tier labels (5-tier system)
  medium_low: 'Medium-Low Risk',
  medium_high: 'Medium-High Risk',
  very_high: 'Very High Risk',

  // FATF statuses
  member: 'FATF Member',
  monitored: 'Enhanced Monitoring',
  grey_list: 'FATF Grey List',
  black_list: 'FATF Black List',
  not_listed: 'Not Listed',

  // SAR / suspicious activity types
  layering: 'Layering',
  placement: 'Placement',
  integration: 'Integration',
  terrorist_financing: 'Terrorist Financing',
  proliferation_financing: 'Proliferation Financing',
  fraud: 'Fraud',
  tax_evasion: 'Tax Evasion',
  money_laundering: 'Money Laundering',
  other_suspicious: 'Other Suspicious Activity',

  // EDD statuses
  not_required: 'Not Required',
  requested: 'Requested',
  submitted: 'Submitted',
  under_assessment: 'Under Assessment',
  satisfied: 'Satisfied',
  failed: 'Failed',

  // Client / account statuses
  restricted: 'Restricted',
  frozen: 'Frozen',
  suspended: 'Suspended',
  terminated: 'Terminated',
  awaiting_documents: 'Awaiting Documents',
  compliance_review: 'Compliance Review',
  not_found: 'Not Found',

  // Payment / investigation statuses and outcomes
  new: 'New',
  assigned: 'Assigned',
  escalated_to_compliance: 'Escalated to Compliance',
  awaiting_response: 'Awaiting Response',
  pending_response: 'Pending Response',
  awaiting_info: 'Awaiting Information',
  filed_sar: 'SAR Filed',
  returned: 'Returned',
  funds_returned: 'Funds Returned',
  partially_returned: 'Partially Returned',
  funds_not_available: 'Funds Not Available',
  traced_only: 'Trace Only',
  no_action_required: 'No Action Required',

  // Pricing / fee engine labels
  monthly_account: 'Monthly Account Fee',
  account_opening: 'Account Opening Fee',
  incoming_payment: 'Incoming Payment',
  outgoing_payment: 'Outgoing Payment',
  fx_fee: 'FX Fee',
  internal_transfer: 'Internal Transfer',
  fixed_plus_percentage: 'Fixed + Percentage',
  fixed_discount: 'Fixed Discount',
  percentage_discount: 'Percentage Discount',

  // Rails / payment methods
  sepa: 'SEPA',
  sepa_sct: 'SEPA SCT',
  sepa_sct_inst: 'SEPA Instant',
  sepa_dd: 'SEPA Direct Debit',
  sepa_b2b: 'SEPA B2B Direct Debit',
  faster: 'Faster Payments',
  gb_fps: 'UK Faster Payments',
  ach: 'ACH',
  us_ach: 'US ACH',
  fednow: 'FedNow',
  wire: 'Wire Transfer',
  swift: 'SWIFT',
  cc_swift: 'Currencycloud SWIFT',
  cc_local: 'Currencycloud Local',
  cc_priority: 'Currencycloud Priority',
  cc_regular: 'Currencycloud Regular',
  swift_mt103: 'SWIFT MT103',
  internal: 'Internal Transfer',
  p2p: 'P2P Transfer',

  // Card product / program types
  debit: 'Debit',
  prepaid: 'Prepaid',
  credit: 'Credit',
  virtual: 'Virtual',
  physical: 'Physical',

  // Industry codes (additional)
  consulting: 'Consulting',
  saas_technology: 'SaaS / Technology',
  professional_services: 'Professional Services',
  real_estate: 'Real Estate',
  gambling: 'Gambling',
  crypto_exchange: 'Cryptocurrency Exchange',
  crypto_services: 'Cryptocurrency Services',
  money_service_business: 'Money Service Business',
  import_export: 'Import / Export',
};

const TOKEN_LABELS: Record<string, string> = {
  FEE: 'Fee',
  IN: 'In',
  OUT: 'Out',
  CC: 'CC',
  ANY: 'Any',
  USACH: 'US ACH',
  GBFPS: 'UK Faster Payments',
  SEPASCT: 'SEPA SCT',
  SEPAINST: 'SEPA Instant',
  INTERNALTRANSFER: 'Internal Transfer',
  INTERNALFX: 'Internal FX',
  FX: 'FX',
  P2P: 'P2P',
  STD: 'Standard',
  ACH: 'ACH',
  SEPA: 'SEPA',
  SWIFT: 'SWIFT',
  FPS: 'FPS',
  BIC: 'BIC',
  IBAN: 'IBAN',
  MCC: 'MCC',
  KYC: 'KYC',
  KYB: 'KYB',
  AML: 'AML',
  PEP: 'PEP',
  UBO: 'UBO',
  SAR: 'SAR',
  STR: 'STR',
  SCA: 'SCA',
  PSD2: 'PSD2',
  '3DS': '3DS',
  EUR: 'EUR',
  GBP: 'GBP',
  USD: 'USD',
};

function formatToken(token: string): string {
  const upper = token.toUpperCase();
  if (TOKEN_LABELS[upper]) return TOKEN_LABELS[upper];
  if (/^\d+$/.test(token)) return token;
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

/**
 * Converts a technical key into a human-readable label.
 * Falls back to title-casing the key if no mapping exists.
 */
export function formatLabel(key: string | null | undefined): string {
  if (!key) return '—';

  const normalized = String(key).trim();
  if (!normalized) return '—';
  if (LABEL_MAP[normalized]) return LABEL_MAP[normalized];

  return normalized
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(formatToken)
    .join(' ');
}
